import { Button, Radio, Skeleton, Spin, Tooltip } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useShippingCarriers } from '../../hooks/useShippingCarriers';
import { useRbac } from '@/hooks/useRbac';
import { ROUTES } from '@/constants/routes';
import { isEstimatableCarrierCode } from '@/constants/shippingCarriers';
import { describeShippingCodLimit } from '@tcr-erp/shared';
import type { CarrierFeeState } from '../../hooks/useCarrierFeeEstimates';

interface Props {
  value?: string;
  onChange: (code: string) => void;
  hasError?: boolean;
  /** Phí hãng vừa báo, khoá theo `code` của hãng. Vắng mặt ⇒ chưa hỏi hãng nào. */
  feeByCarrier?: Map<string, CarrierFeeState>;
  /** Nút làm mới kiêm luôn việc tính lại phí. */
  onReloadFees?: () => void;
  /**
   * Chỉ xem (màn sửa đơn, đơn đã chốt giỏ): khoá chọn hãng, bỏ nút làm mới và
   * bỏ link "Kết nối ĐVVC" — người xem một đơn đã chốt không có việc gì phải
   * đổi hãng hay đi bật hãng mới từ đây.
   */
  disabled?: boolean;
}

function FeeCell({
  state,
  supported,
}: {
  state?: CarrierFeeState;
  /** Hãng có nằm trong `ESTIMATABLE_SHIPPING_CARRIER_CODES` không. */
  supported: boolean;
}): JSX.Element | null {
  // Kiểm TRƯỚC `state`: hãng không hỗ trợ thì `state` luôn `undefined` (không
  // ai hỏi hãng đó bao giờ), và đó là một sự thật KHÁC hẳn "đơn chưa đủ dữ
  // liệu" — gộp chung hai trường hợp là nói dối thu ngân rằng thiếu thông tin
  // trong khi thực ra hãng này chưa bao giờ tính được phí.
  if (!supported) {
    return <span style={{ fontSize: 11, color: '#bfbfbf' }}>Chưa hỗ trợ ước tính phí</span>;
  }
  if (!state) {
    return <span style={{ fontSize: 11, color: '#bfbfbf' }}>Cần địa chỉ và khối lượng</span>;
  }
  if (state.status === 'loading') return <Spin size="small" />;
  if (state.status === 'ok' && state.fee !== undefined) {
    return (
      <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
        {state.fee.toLocaleString('vi-VN')} đ
      </span>
    );
  }
  return (
    <Tooltip title={state.message ?? 'Hãng không trả về phí'}>
      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
        {state.status === 'timeout' ? 'Hãng phản hồi chậm' : 'Không tính được'}
      </span>
    </Tooltip>
  );
}

/**
 * Danh sách hãng vận chuyển. Hai trạng thái được hiện:
 *  - connected    → chọn được
 *  - connectable  → Settings bật được → link sang Settings, NHƯNG chỉ hiện
 *                   cho người có quyền `settings.manage`. Cấu hình
 *                   `shipping_providers` nằm ở `ROUTES.settings.general`, route
 *                   đó đòi quyền `settings.manage` (App.tsx) — thu ngân thường
 *                   không có. Đẩy một người không có quyền vào trang 403 là ngõ
 *                   cụt, nên họ chỉ thấy chữ xám "Chưa kết nối".
 *
 * `coming_soon` (Settings CHƯA hỗ trợ) bị ẨN hẳn: hàng đó không bấm được, không
 * kết nối được, chỉ chiếm bốn dòng giữa panel hẹp. Lọc ở ĐÂY chứ không ở
 * `useShippingCarriers`: `POS/index.tsx` và `Orders/edit.tsx` cùng đọc hook đó
 * để tra `codLimit` theo mã hãng — đơn cũ đã đặt bằng một hãng nay thành
 * `coming_soon` mà bị lọc khỏi hook thì tra ra `undefined` và mất luôn hạn mức
 * COD, im lặng.
 */
export function CarrierList({ value, onChange, hasError, feeByCarrier, onReloadFees, disabled }: Props): JSX.Element {
  const { carriers, isLoading, refetch } = useShippingCarriers();
  const navigate = useNavigate();
  const { hasPermission } = useRbac();
  const canManageSettings = hasPermission('settings.manage');

  // Hãng đã kết nối lên đầu — đó là những hãng bấm chọn được, thu ngân không
  // phải lướt qua mấy dòng xám mới tới. Trong cùng một nhóm vẫn giữ nguyên thứ
  // tự catalog (`SHIPPING_CARRIERS`, hãng dùng nhiều nhất trước): `sort` của JS
  // ổn định nên chỉ cần so theo `enabled`. `.filter` đã trả mảng mới nên `sort`
  // không đụng vào mảng của hook.
  const visibleCarriers = carriers
    .filter((c) => c.status !== 'coming_soon')
    .sort((a, b) => Number(b.enabled) - Number(a.enabled));
  const hasConnected = carriers.some((c) => c.enabled);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#333', flex: 1 }}>Đối tác vận chuyển</span>
        {!disabled && (
          <Tooltip title="Làm mới danh sách đơn vị vận chuyển">
            <Button
              aria-label="Làm mới danh sách đơn vị vận chuyển"
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => {
                refetch();
                onReloadFees?.();
              }}
            />
          </Tooltip>
        )}
      </div>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <>
          {!hasConnected && (
            <div style={{ fontSize: 11, color: '#faad14' }}>Chưa kết nối đơn vị vận chuyển nào</div>
          )}
          <Radio.Group
            value={value}
            onChange={(e: RadioChangeEvent) => onChange(e.target.value as string)}
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
            {visibleCarriers.map((c) => (
              <div
                key={c.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Radio value={c.code} disabled={disabled || !c.enabled} style={{ flex: 1, marginInlineEnd: 0 }}>
                  <span style={{ fontSize: 12, color: c.enabled ? '#333' : '#999' }}>{c.label}</span>
                  {/* Chỉ hiện khi hãng thật sự có hạn mức — không có gì để nói
                      thì đừng chiếm chỗ. Thu ngân cần biết TRƯỚC khi chọn, chứ
                      không phải lúc bấm tạo đơn rồi mới bị chặn. */}
                  {describeShippingCodLimit(c.codLimit) && (
                    <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.4 }}>
                      {describeShippingCodLimit(c.codLimit)}
                    </div>
                  )}
                </Radio>
                {c.enabled && (
                  <FeeCell
                    state={feeByCarrier?.get(c.code)}
                    supported={isEstimatableCarrierCode(c.code)}
                  />
                )}
                {c.status === 'connectable' && !disabled &&
                  (canManageSettings ? (
                    <Button
                      aria-label={`Kết nối ĐVVC cho ${c.label}`}
                      type="link"
                      size="small"
                      style={{ padding: 0, fontSize: 11 }}
                      onClick={() => navigate(ROUTES.settings.general)}
                    >
                      Kết nối ĐVVC
                    </Button>
                  ) : (
                    <span style={{ fontSize: 11, color: '#bfbfbf' }}>Chưa kết nối</span>
                  ))}
              </div>
            ))}
          </Radio.Group>
        </>
      )}

      {hasError && !value && (
        <div role="alert" style={{ color: '#ff4d4f', fontSize: 11 }}>
          Vui lòng chọn đơn vị vận chuyển
        </div>
      )}
    </div>
  );
}
